import React, { useState } from 'react';
import { styled, keyframes } from '@compiled/react';
import Button from '@atlaskit/button';
import Lozenge from '@atlaskit/lozenge';
import Popup from '@atlaskit/popup';
import { Inline, Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import ToolTip from '@atlaskit/tooltip';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useChatOpsEntitlement } from '../../../services/main';
import type { AppNameType, ICommunicationConfig, TsiteConnectionData } from '../../types';
import { ExpandButton } from '../expand-button';
import { FeatureToggle } from '../feature-toggle-v2';
import { AppIcon } from '../index';
import { UpgradePlanContent } from '../upgrade-plan-content';
import messages from './messages';

/* eslint-disable no-nested-ternary */

type Props = {
	siteConnectionData: TsiteConnectionData | undefined;
	isSiteAdmin: boolean;
	isCommunicationEnabled: boolean | undefined;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onToggleChangeHandler: any;
	config: ICommunicationConfig;
	toggleDisabled: boolean;
	isLoading: boolean;
};

type LastContentProps = {
	configTooltip: ICommunicationConfig['tooltip'];
	appName: AppNameType;
};

export const LastContent = ({ configTooltip, appName }: LastContentProps) => {
	const { formatMessage } = useIntl();
	return (
		<LoadingLastContent>
			<ToolTip content={formatMessage(configTooltip.content)} position="bottom">
				<Button
					appearance="link"
					testId={`servicedesk-incident-management-settings.common.ui.communication-card.test-admin-connect-${appName}-button`}
					onClick={(event, analyticsEvent) => {
						fireUIAnalytics(
							analyticsEvent,
							configTooltip.button.analyticEvent.name,
							configTooltip.button.analyticEvent.config,
						);
					}}
					href={configTooltip.button.href}
					target="_blank"
				>
					{formatMessage(configTooltip.button.text)}
				</Button>
			</ToolTip>
		</LoadingLastContent>
	);
};

export const CommunicationCard = ({
	siteConnectionData,
	isSiteAdmin,
	isCommunicationEnabled,
	onToggleChangeHandler,
	toggleDisabled,
	config,
	isLoading,
}: Props) => {
	const { formatMessage } = useIntl();
	const [{ chatOpsEntitlementEnabled }] = useChatOpsEntitlement();
	const [isPaymentPopperOpen, setIsPaymentPopperOpen] = useState(false);
	const [isExpanded, setIsExpanded] = useState(false);
	const isChatOpsDisabled =
		!siteConnectionData && !chatOpsEntitlementEnabled && ff('chatops-pp-banners_1o319');

	return (
		<>
			{isLoading && (
				<Spacing>
					<SettingCardV2>
						<Inline spread="space-between" alignBlock="center">
							<Inline alignBlock="center">
								<SkeletonLines />

								<SkeletonLines />
							</Inline>
							<SkeletonLines />
						</Inline>
					</SettingCardV2>
				</Spacing>
			)}
			{!isLoading && (
				<Spacing>
					<SettingCardV2>
						<Inline spread="space-between" alignBlock="center">
							<Inline alignBlock="center">
								{ff('jfe-asset-consolidation-itsm-agni_po996') ? (
									<Box
										// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
										xcss={AppIconContainerStyles(isChatOpsDisabled)}
									>
										<config.appIconComponent label={`${config.appName}-logo`} />
									</Box>
								) : (
									<AppIcon src={config.appIcon} alt={`${config.appName}-logo`} />
								)}

								<TextStyle isChatOpsDisabled={isChatOpsDisabled}>{config.appName}</TextStyle>

								<Lozenge
									appearance={
										!siteConnectionData || !isCommunicationEnabled ? 'default' : 'success'
									}
								>
									{siteConnectionData
										? formatMessage(
												isCommunicationEnabled ? config.appStatusEnabled : config.appStatusDisabled,
											)
										: formatMessage(config.appStatusNotConnected)}
								</Lozenge>
							</Inline>
							{!siteConnectionData ? (
								ff('chatops-pp-banners_1o319') && !chatOpsEntitlementEnabled ? (
									<Popup
										isOpen={isPaymentPopperOpen}
										onClose={() => setIsPaymentPopperOpen(false)}
										placement="bottom-end"
										content={() => (
											<Box xcss={upgradePopperStyles}>
												<UpgradePlanContent />
											</Box>
										)}
										trigger={(triggerProps) => (
											// eslint-disable-next-line react/jsx-props-no-spreading
											<Box {...triggerProps}>
												<ExpandButton
													isDisabled={!chatOpsEntitlementEnabled}
													label={formatMessage(messages.noZoomAppConnected)}
													onClick={() => {
														setIsPaymentPopperOpen(
															!chatOpsEntitlementEnabled && !isPaymentPopperOpen,
														);
														setIsExpanded(chatOpsEntitlementEnabled && !isExpanded);
													}}
													isExpanded={chatOpsEntitlementEnabled && isExpanded}
												/>
											</Box>
										)}
									/>
								) : (
									<LastContent
										configTooltip={isSiteAdmin ? config.adminTooltip : config.tooltip}
										appName={config.appName}
									/>
								)
							) : (
								<>
									{!chatOpsEntitlementEnabled ? (
										<FeatureToggle
											disabled
											feature="incident-management"
											popperText={<UpgradePlanContent />}
											focusToggle
										/>
									) : (
										<Toggle
											size="large"
											isChecked={isCommunicationEnabled}
											isDisabled={toggleDisabled}
											label={formatMessage(config.toogle.label)}
											testId={`servicedesk-incident-management-settings.common.ui.communication-card.test-connect-${config.appName}-to-project-toggle`}
											onChange={onToggleChangeHandler}
										/>
									)}
								</>
							)}
						</Inline>
					</SettingCardV2>
				</Spacing>
			)}
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LoadingLastContent = styled.div({
	display: 'flex',
	alignItems: 'center',
	marginRight: token('space.100', '10px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Spacing = styled.div({
	marginBottom: token('space.100', '8px'),
});

const shimmer = keyframes({
	'0%': {
		backgroundPosition: '-300px 0',
	},
	'100%': {
		backgroundPosition: '2000px 0',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SkeletonLines = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 30}px`,
	height: token('space.200', '16px'),
	marginTop: token('space.050', '4px'),
	marginLeft: token('space.150', '12px'),
	animationDuration: '1s',
	animationFillMode: 'forwards',
	animationIterationCount: 'infinite',
	animationName: shimmer,
	animationTimingFunction: 'linear',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.skeleton.subtle', colors.N30),
	backgroundImage: `linear-gradient(
        to right,
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
        ${token('color.skeleton.subtle', colors.N30)} 10%,
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
        ${token('color.skeleton', colors.N20)} 30%,
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
        ${token('color.skeleton.subtle', colors.N30)} 50%
    )`,
	backgroundRepeat: 'no-repeat',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextStyle = styled.h4<{ isChatOpsDisabled: boolean }>({
	margin: `0 ${token('space.300', '24px')} 0 ${token('space.150', '12px')}`,
	paddingRight: token('space.1000', '80px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	color: ({ isChatOpsDisabled }) =>
		isChatOpsDisabled ? token('color.text.disabled') : token('color.text', 'inherit'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SettingCardV2 = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 5}px`,
	borderRadius: '3px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface.raised', colors.N0),
	boxShadow: token(
		'elevation.shadow.raised',
		'0 1px 1px 0 rgb(9 30 66 / 25%), 0 0 1px 0 rgb(9 30 66 / 31%)',
	),
	boxSizing: 'border-box',
	paddingRight: token('space.100', '8px'),
	paddingTop: token('space.050', '4px'),
});

const AppIconContainerStyles = (isChatOpsDisabled: boolean) =>
	xcss({
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		marginLeft: 'space.150',
		height: token('space.300', '24px'),
		width: token('space.300', '24px'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		opacity: isChatOpsDisabled ? 0.5 : 1,
	});

const upgradePopperStyles = xcss({ padding: 'space.150', width: '300px' });
